import type { ColumnDef } from '@tanstack/react-table';
import type { WorkflowBlueprint } from '../types';
import { DataTableColumnHeader } from '@/components/ui/data-table';
import { Switch } from '@/components/ui/switch';
import { formatIntentLabel, isTemplateEnabled } from '../utils';

interface ActionIntentsColumnsLabels {
  intentHeader?: string;
  subIntentsHeader?: string;
  subIntentsSuffix?: string;
  enabledHeader?: string;
}

interface ActionIntentsColumnsOptions {
  canEdit: boolean;
  pendingId: string | null;
  onToggleEnabled: (blueprint: WorkflowBlueprint, enabled: boolean) => void;
  labels?: ActionIntentsColumnsLabels;
}

export function getActionIntentsColumns({
  canEdit,
  pendingId,
  onToggleEnabled,
  labels
}: ActionIntentsColumnsOptions): ColumnDef<WorkflowBlueprint>[] {
  const intentHeader = labels?.intentHeader ?? 'Action Intent';
  const subIntentsHeader = labels?.subIntentsHeader ?? 'Sub-intents';
  const subIntentsSuffix = labels?.subIntentsSuffix ?? 'sub-intents on';
  const enabledHeader = labels?.enabledHeader ?? 'Enabled';

  return [
    {
      accessorKey: 'intent',
      header: ({ column }) => <DataTableColumnHeader column={column} title={intentHeader} />,
      cell: ({ row }) => (
        <div className='flex flex-col'>
          <span className='font-medium text-foreground' title={row.original.intent}>
            {formatIntentLabel(row.original.intent)}
          </span>
        </div>
      ),
      size: 320
    },
    {
      id: 'subIntents',
      header: ({ column }) => <DataTableColumnHeader column={column} title={subIntentsHeader} />,
      cell: ({ row }) => {
        const total = row.original.templates.length;
        const on = row.original.templates.filter((_, i) => isTemplateEnabled(row.original.templatesEnabled, i)).length;
        return (
          <span className='text-sm text-muted-foreground'>
            <span className='font-medium text-foreground'>{on}</span> / {total} {subIntentsSuffix}
          </span>
        );
      },
      enableSorting: false,
      size: 160
    },
    {
      id: 'enabled',
      header: enabledHeader,
      cell: ({ row }) => {
        const blueprint = row.original;
        return (
          <div className='flex items-center' onClick={(e) => e.stopPropagation()}>
            <Switch
              className='cursor-pointer'
              checked={blueprint.enabled}
              onCheckedChange={(checked) => onToggleEnabled(blueprint, checked)}
              disabled={!canEdit || pendingId === blueprint.id}
            />
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 100,
      maxSize: 100
    }
  ];
}
